<template>
	<div class="pdp-stat-box" :class="classes">
		<!-- The eyebrow label may or may not have a tooltip attached. -->
		<p class="eyebrow">
			<template v-if="labelTip">
				<span
					:title="labelTip"
					:data-width="tooltipAutoWidth ? 'auto-width' : ''"
					>{{ label }}</span
				>
			</template>

			<template v-else-if="hasCustomTooltipSlot">
				<tooltip
					:text="label"
					:data-width="tooltipAutoWidth ? 'auto-width' : ''"
					:openModalInSmall="openTooltipModalInSmall"
				>
					<template slot="customTooltip">
						<slot name="customTooltip" />
					</template>
				</tooltip>
			</template>

			<template v-else-if="hasCustomLabelSlot">
				<slot name="customLabel" />
			</template>

			<template v-else>
				{{ label }}
			</template>

			<template v-if="hasCustomFootnoteSlot">
				<slot name="customFootnote" />
			</template>
		</p>

		<!-- Normally, you'll just render stat.value here. -->
		<p
			v-if="value !== null"
			:class="[small ? 'p1' : 'h4', flowDown ? 'flowDown' : '']"
		>
			<span class="visually-hidden">{{ ariaLabel }}</span>
			<span
				class="icon icon-arrow-up stat-arrow"
				role="img"
				aria-label="Up"
				v-if="increasing"
			/>
			<span
				class="icon icon-arrow-down stat-arrow"
				role="img"
				aria-label="Down"
				v-else-if="decreasing"
			/>
			{{ value }}
		</p>

		<!-- customContent is used when you need html in a stat,
  for example to render the Morningstar rating icons. -->
		<p v-else :class="[small ? 'p1' : 'h4', flowDown ? 'flowDown' : '']">
			<span class="visually-hidden">{{ ariaLabel }}</span>
			<slot name="customContent" />
		</p>

		<!-- Optional caption usually shows "as of [date]" type info -->
		<p class="caption" v-if="caption" v-html="caption" />
	</div>
</template>

<script>
import Tooltip from "../tooltip/Tooltip";

/**
 * A statbox component.
 */
export default {
	name: "StatBox",
	props: {
		/**
		 * Adds custom class(es) to the Stat Box component wrapper div.
		 */
		classes: {
			type: String,
			default: ""
		},

		/**
		 * When populated, adds a tooltip to the stat label and is the content of the tooltip bubble.
		 */
		labelTip: {
			type: String,
			default: ""
		},

		/**
		 * When `true`, the tooltip bubble window sizes to the content rather than the component's fixed sizes based on character count.
		 */
		tooltipAutoWidth: {
			type: Boolean,
			required: false
		},

		/**
		 * The title of the stat.
		 */
		label: {
			type: String,
			default: ""
		},

		/**
		 * The content for the `ariaLabel` on the stat box label.
		 */
		ariaLabel: {
			type: String,
			default: ""
		},

		/**
		 * When `true`, the stat number appears with a smaller font.
		 */
		small: {
			type: [Boolean, String],
			default: ""
		},

		/**
		 * When `true`, the flex-direction becomes `column` for the stat box content.
		 */
		flowDown: {
			type: [Boolean, String],
			default: ""
		},

		/**
		 * When `true`, a green up arrow appears to the left of the stat.
		 */
		increasing: {
			type: [Boolean, String],
			default: ""
		},

		/**
		 * When `true`, a red down arrow appears to the left of the stat.
		 */
		decreasing: {
			type: [Boolean, String],
			default: ""
		},

		/**
		 * The stat value.
		 */
		value: {
			type: [String, Number],
			default: null
		},

		/**
		 * A description that appears below the stat.
		 */
		caption: {
			type: String,
			default: ""
		},

		/**
		 * When `true` and if using the `labelTip` prop, the tooltip bubble will open as a modal in the small viewport.
		 */
		openTooltipModalInSmall: {
			type: Boolean,
			default: false
		}
	},

	components: { Tooltip },

	computed: {
		hasCustomTooltipSlot() {
			return !!this.$slots.customTooltip;
		},
		hasCustomLabelSlot() {
			return !!this.$slots.customLabel;
		},
		hasCustomFootnoteSlot() {
			return !!this.$slots.customFootnote;
		}
	}
};
</script>

<style lang="scss">
.pdp-stat-box {
	align-content: flex-end;
	border-bottom: 1px solid $color--light-gray;
	display: inline-flex;
	flex-wrap: wrap;
	justify-content: space-between;
	padding-bottom: 20px;
	padding-top: 30px;
	width: 100%;

	.eyebrow {
		flex-basis: 100%;
		width: 100%;

		span[title],
		span[data-tooltip-trigger] {
			line-height: 1.4;
		}
	}

	// This aligns the stat if there's an arrow icon and the stat's on two lines
	.h4,
	.p1 {
		display: flex;
		margin-bottom: 0;
	}

	.h4 {
		white-space: nowrap;
	}

	.flowDown {
		flex-direction: column;
	}

	.icon {
		margin-right: 8px;
	}

	.stat-arrow {
		font-size: 16px;
		line-height: 28px;
	}

	@include to-md {
		align-items: flex-end;
		flex-basis: 100%;
		width: 100%;
	}

	// 4 columns in the middle view
	@include from-md {
		border: none;
		padding-right: 20px;

		.caption {
			flex-basis: 100%;
		}
	}

	// this styles a superscript item in the statbox slot area
	.superscript {
		font-size: 12px;
		line-height: 14px;
		height: 14px;
		margin-left: -4px;
	}
}

// Some stat boxes are used in modals, and some of those
// have a link where the value would normally be,
// so we need to override the default styles for `cta`.
// Note that the font size for these modal stat values is smaller.
.stat-cta {
	font-size: 20px;
}
</style>
